import { ref } from 'vue'
import { FirebaseProvider } from '../infrastructure/providers/FirebaseProvider'
import { doc, getDoc, updateDoc, collection, getDocs, query, orderBy, limit, addDoc, Timestamp, where, arrayUnion, writeBatch } from 'firebase/firestore'
import { useCompanyStore } from '../stores/company'
import { TrackingService } from '../application/services/TrackingService'
import { ShipmentService } from '../application/services/ShipmentService'
import { FirestoreShipmentRepository } from '../infrastructure/repositories/FirestoreShipmentRepository'
import { useAuth } from './useAuth'
import type { Shipment } from '../domain/entities/Shipment'
import { resolveMimeType } from '../utils/mimeType'
import { assertShipmentWritable } from '../utils/shipmentLock'
import { pickOwnSentEmail } from '../utils/email'
import { threadSubjectKey } from '../utils/emailSubject'
import { useFirestoreEmailStore } from '../stores/firestoreEmail'

/** Máximo de escrituras por writeBatch (Firestore permite 500; dejamos margen). */
const BATCH_LIMIT = 400

/** Resultado de propagar una asociación: escritos, saltados por estar en otro embarque y fallidos. */
export type LinkResult = { linked: number; skipped: number; failed: number }

export function useEmailAssociation() {
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const companyStore = useCompanyStore()
  const { currentUser } = useAuth()
  const db = FirebaseProvider.getInstance().getFirestore()
  const trackingService = new TrackingService(new FirestoreShipmentRepository())

  const associateEmailToShipment = async (emailId: string, shipmentId: string, shipmentIdNumber: string) => {
    try {
      isLoading.value = true
      error.value = null

      if (!companyStore.selectedCompanyId) {
        throw new Error('No company selected')
      }

      const emailRef = doc(db, 'Companies', companyStore.selectedCompanyId, 'emails', emailId)
      const emailSnap = await getDoc(emailRef)

      if (!emailSnap.exists()) {
        throw new Error('Email no encontrado')
      }

      await assertShipmentWritable(db, companyStore.selectedCompanyId, shipmentId)

      const emailData = emailSnap.data()
      const receivedAt = emailData.receivedAt || null

      // Asociar el email al embarque
      await updateDoc(emailRef, {
        matchedShipmentId: shipmentId,
        matchedShipmentIdNumber: shipmentIdNumber,
        updatedAt: new Date()
      })

      // Leer el embarque para obtener el 3PL
      const shipmentRef = doc(db, 'Companies', companyStore.selectedCompanyId, 'shipments', shipmentId)
      const shipmentSnap = await getDoc(shipmentRef)
      const shipmentData = shipmentSnap.exists() ? shipmentSnap.data() : {}
      const pl3 = shipmentData.pl3 || ''

      // Actualizar el embarque con los campos de envio de documentos originales
      const shipmentUpdate: Record<string, any> = {
        envio_documentos_originales_proveedor: pl3,
        updatedAt: Timestamp.now()
      }

      if (receivedAt) {
        if (receivedAt instanceof Date) {
          shipmentUpdate.envio_documentos_originales_fecha = Timestamp.fromDate(receivedAt)
        } else if (typeof receivedAt === 'string') {
          shipmentUpdate.envio_documentos_originales_fecha = Timestamp.fromDate(new Date(receivedAt))
        } else if (receivedAt && typeof receivedAt === 'object' && 'seconds' in receivedAt) {
          shipmentUpdate.envio_documentos_originales_fecha = receivedAt
        }
      }

      await updateDoc(shipmentRef, shipmentUpdate)

      // Datos del correo ANTES de asociarlo: associateThreadToShipment los usa sin volver a leer.
      return { success: true, email: { ...emailData, id: emailId } as Record<string, any> }
    } catch (err) {
      console.error('Error asociando email a embarque:', err)
      error.value = err instanceof Error ? err.message : 'Error desconocido'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Escribe la asociación en los correos sin embarque, en los que ya están en este
   * embarque y en los de `movableFrom` (el embarque anterior del ancla, para que
   * reasignar no parta el hilo). Nunca pisa otro embarque: esos cuentan en `skipped`.
   * Si un lote falla sigue con los demás, así `linked` refleja lo que sí quedó escrito.
   */
  const linkUnassignedEmails = async (
    companyId: string,
    emails: any[],
    shipmentId: string,
    shipmentIdNumber: string,
    movableFrom?: string
  ): Promise<LinkResult> => {
    const free = emails.filter(e => !e.matchedShipmentId || e.matchedShipmentId === shipmentId || (movableFrom && e.matchedShipmentId === movableFrom))
    const result: LinkResult = { linked: 0, skipped: emails.length - free.length, failed: 0 }
    for (let i = 0; i < free.length; i += BATCH_LIMIT) {
      const chunk = free.slice(i, i + BATCH_LIMIT)
      const batch = writeBatch(db)
      for (const e of chunk) {
        batch.update(doc(db, 'Companies', companyId, 'emails', e.id), {
          matchedShipmentId: shipmentId,
          matchedShipmentIdNumber: shipmentIdNumber,
          updatedAt: new Date()
        })
      }
      try {
        await batch.commit()
        result.linked += chunk.length
      } catch (err) {
        result.failed += chunk.length
        console.warn('[useEmailAssociation] No se pudo escribir un lote de asociaciones', err)
      }
    }
    return result
  }

  /** Usa los correos que el buzón ya tiene en memoria; solo va a Firestore por los que falten. */
  const readEmails = async (companyId: string, emailIds: string[]): Promise<any[]> => {
    const firestoreEmailStore = useFirestoreEmailStore()
    const known = emailIds.map(id => firestoreEmailStore.getRawEmailData(id)).filter(Boolean)
    const knownIds = new Set(known.map(e => e.id))
    const snaps = await Promise.all(
      emailIds.filter(id => !knownIds.has(id)).map(id => getDoc(doc(db, 'Companies', companyId, 'emails', id)))
    )
    return [...known, ...snaps.filter(s => s.exists()).map(s => ({ id: s.id, ...s.data() }))]
  }

  /**
   * Asocia el correo ancla y le pasa el embarque a los demás correos del hilo (KILA-522).
   * Hilo = mismo conversationId o candidateIds (la UI agrupa por asunto), pero SIEMPRE con el
   * mismo asunto normalizado: Outlook mantiene el conversationId aunque cambie la operación y
   * así se pegaron correos ajenos en KILA-467.
   * Si falla el ancla lanza; si falla después, el ancla ya quedó y se devuelve `incomplete`.
   */
  const associateThreadToShipment = async (
    anchorEmailId: string,
    shipmentId: string,
    shipmentIdNumber: string,
    candidateIds: string[] = []
  ): Promise<LinkResult & { incomplete: boolean }> => {
    // Se fija una sola vez: si el usuario cambia de compañía a mitad, no se escribe en la otra.
    const companyId = companyStore.selectedCompanyId
    if (!companyId) throw new Error('No company selected')
    const { email: anchor } = await associateEmailToShipment(anchorEmailId, shipmentId, shipmentIdNumber)

    try {
      const key = threadSubjectKey(anchor.subject)
      if (!key) return { linked: 0, skipped: 0, failed: 0, incomplete: false }

      const byId = new Map<string, any>()
      if (anchor.conversationId) {
        const emailsRef = collection(db, 'Companies', companyId, 'emails')
        const convSnapshot = await getDocs(query(emailsRef, where('conversationId', '==', anchor.conversationId)))
        convSnapshot.docs.forEach(d => byId.set(d.id, { id: d.id, ...d.data() }))
      }
      const missing = candidateIds.filter(id => id !== anchorEmailId && !byId.has(id))
      ;(await readEmails(companyId, missing)).forEach(e => byId.set(e.id, e))
      byId.delete(anchorEmailId)

      const thread = [...byId.values()].filter(e => e.folder !== 'trash' && threadSubjectKey(e.subject) === key)
      const result = await linkUnassignedEmails(companyId, thread, shipmentId, shipmentIdNumber, anchor.matchedShipmentId)
      return { ...result, incomplete: false }
    } catch (err) {
      console.warn('[useEmailAssociation] El ancla quedó asociada pero no se pudo propagar al hilo', err)
      return { linked: 0, skipped: 0, failed: 0, incomplete: true }
    }
  }

  const createBlankShipmentFromEmail = async (emailId: string) => {
    try {
      isLoading.value = true
      error.value = null

      if (!companyStore.selectedCompanyId) {
        throw new Error('No company selected')
      }

      if (!currentUser.value) {
        throw new Error('User not authenticated')
      }

      const trackingRequest = {
        type: 'blank',
        shipmentNumber: null,
        carrier: null
      }

      const response = await trackingService.getTracking(
        trackingRequest,
        companyStore.selectedCompanyId,
        currentUser.value.uid
      )

      if (response.success && response.data?.shipmentId) {
        const shipmentId = response.data.shipmentId

        const shipmentRef = doc(db, 'Companies', companyStore.selectedCompanyId, 'shipments', shipmentId)
        const shipmentSnapshot = await getDoc(shipmentRef)

        let shipmentIdNumber = shipmentId
        if (shipmentSnapshot.exists()) {
          const shipmentData = shipmentSnapshot.data() as Shipment
          shipmentIdNumber = shipmentData?.idNumber || shipmentId

          if (!shipmentData?.status) {
            const initialStatus = await ShipmentService.resolveCreationStatus(companyStore.selectedCompanyId)
            await updateDoc(shipmentRef, { status: initialStatus })
          }
        }

        await associateEmailToShipment(emailId, shipmentId, shipmentIdNumber)

        return {
          success: true,
          shipmentId,
          shipmentIdNumber
        }
      } else {
        throw new Error(response.error?.message || 'Error creando embarque')
      }
    } catch (err) {
      console.error('Error creando embarque desde email:', err)
      error.value = err instanceof Error ? err.message : 'Error desconocido'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const fetchAvailableShipments = async (maxResults = 100) => {
    try {
      isLoading.value = true
      error.value = null

      if (!companyStore.selectedCompanyId) {
        throw new Error('No company selected')
      }

      const shipmentsRef = collection(db, 'Companies', companyStore.selectedCompanyId, 'shipments')
      const q = query(shipmentsRef, orderBy('createdAt', 'desc'), limit(maxResults))
      const snapshot = await getDocs(q)

      const shipments = snapshot.docs.map(doc => {
        const data = doc.data()
        return {
          id: doc.id,
          idNumber: data.idNumber || doc.id,
          booking: data.booking || '',
          mbl: data.mbl || '',
          hbl: data.hbl || '',
          status: data.status || '',
          origin: data.origin || data.originLocationName || '',
          destination: data.destination || data.destinationLocationName || '',
          createdAt: data.createdAt,
          raw: data
        }
      })

      return shipments
    } catch (err) {
      console.error('Error obteniendo embarques:', err)
      error.value = err instanceof Error ? err.message : 'Error desconocido'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const searchShipments = async (searchTerm: string, referenceFields: string[] = []) => {
    try {
      if (!companyStore.selectedCompanyId) {
        throw new Error('No company selected')
      }

      const shipmentsRef = collection(db, 'Companies', companyStore.selectedCompanyId, 'shipments')
      const term = searchTerm.toUpperCase()

      const prefixQuery = (field: string, value: string) =>
        query(shipmentsRef, where(field, '>=', value), where(field, '<=', value + '\uf8ff'), limit(20))

      const baseFields = ['idNumber', 'booking', 'mbl', 'hbl']
      const queries = baseFields.map(field => prefixQuery(field, term))

      // Campos de referencia activos: sus valores no siempre estan en mayusculas,
      // asi que se consulta el termino tal cual y tambien en mayusculas
      for (const field of referenceFields) {
        if (baseFields.includes(field)) continue
        for (const value of new Set([searchTerm, term])) {
          queries.push(prefixQuery(field, value))
        }
      }

      const snapshots = await Promise.all(queries.map(q => getDocs(q)))

      const seen = new Set<string>()
      const results: any[] = []

      for (const snapshot of snapshots) {
        for (const docSnap of snapshot.docs) {
          if (seen.has(docSnap.id)) continue
          seen.add(docSnap.id)
          const data = docSnap.data()
          results.push({
            id: docSnap.id,
            idNumber: data.idNumber || docSnap.id,
            booking: data.booking || '',
            mbl: data.mbl || '',
            hbl: data.hbl || '',
            status: data.status || '',
            origin: data.origin || data.originLocationName || '',
            destination: data.destination || data.destinationLocationName || '',
            createdAt: data.createdAt,
            raw: data
          })
        }
      }

      return results
    } catch (err) {
      console.error('Error buscando embarques:', err)
      return []
    }
  }

  const fetchShipmentContainersBatch = async (shipmentIds: string[]) => {
    if (!companyStore.selectedCompanyId || shipmentIds.length === 0) return {}

    const result: Record<string, string[]> = {}
    const batchSize = 10
    for (let i = 0; i < shipmentIds.length; i += batchSize) {
      const batch = shipmentIds.slice(i, i + batchSize)
      const promises = batch.map(async (shipmentId) => {
        try {
          const containersRef = collection(db, 'Companies', companyStore.selectedCompanyId!, 'shipments', shipmentId, 'containers')
          const snapshot = await getDocs(containersRef)
          const containers = snapshot.docs.map(d => {
            const data = d.data()
            return data.number || data.containerNumber || ''
          }).filter(Boolean)
          if (containers.length > 0) result[shipmentId] = containers
        } catch { /* skip failed container fetches */ }
      })
      await Promise.all(promises)
    }
    return result
  }

  const fetchShipmentContainers = async (shipmentId: string) => {
    try {
      if (!companyStore.selectedCompanyId) {
        return []
      }

      const containersRef = collection(db, 'Companies', companyStore.selectedCompanyId, 'shipments', shipmentId, 'containers')
      const snapshot = await getDocs(containersRef)

      return snapshot.docs.map(doc => {
        const data = doc.data()
        return data.number || data.containerNumber || ''
      }).filter(Boolean)
    } catch (err) {
      console.error('Error obteniendo containers:', err)
      return []
    }
  }

  /**
   * Copia los archivos adjuntos del email a la subcolección files del embarque.
   * Verifica duplicados por sourceEmailId + fileName para evitar copias repetidas.
   */
  const copyEmailAttachmentsToShipment = async (emailId: string, shipmentId: string) => {
    try {
      if (!companyStore.selectedCompanyId || !currentUser.value) {
        console.warn('No company or user available for copying attachments')
        return { success: false, copiedCount: 0 }
      }

      await assertShipmentWritable(db, companyStore.selectedCompanyId, shipmentId)

      // Obtener archivos de la subcolección files del email
      const emailFilesRef = collection(db, 'Companies', companyStore.selectedCompanyId, 'emails', emailId, 'files')
      const emailFilesSnapshot = await getDocs(emailFilesRef)

      if (emailFilesSnapshot.empty) {
        return { success: true, copiedCount: 0 }
      }

      // Referencia a la subcolección files del embarque
      const shipmentFilesRef = collection(db, 'Companies', companyStore.selectedCompanyId, 'shipments', shipmentId, 'files')

      // Verificar archivos ya copiados desde este email para evitar duplicados
      const existingFromEmailQuery = query(shipmentFilesRef, where('sourceEmailId', '==', emailId))
      const existingSnapshot = await getDocs(existingFromEmailQuery)
      const existingFileNames = new Set(
        existingSnapshot.docs.map(d => d.data().fileName)
      )

      let copiedCount = 0
      let skippedCount = 0
      const copyPromises = emailFilesSnapshot.docs.map(async (fileDoc) => {
        const fileData = fileDoc.data()
        const fileName = fileData.filename || 'archivo_sin_nombre'

        // Saltar si ya existe un archivo con el mismo nombre copiado desde este email
        if (existingFileNames.has(fileName)) {
          skippedCount++
          return
        }

        const shipmentFileData = {
          shipmentId: shipmentId,
          fileType: fileData.fileType || 'other',
          fileName,
          fileUrl: fileData.downloadUrl || '',
          fileSize: fileData.size || 0,
          mimeType: resolveMimeType(fileData.type || fileData.metadata?.originalContentType, fileName),
          uploadedAt: Timestamp.now(),
          uploadedBy: {
            id: currentUser.value?.uid || '',
            name: currentUser.value?.name || 'Sistema',
            lastName: currentUser.value?.lastName || ''
          },
          companyId: companyStore.selectedCompanyId,
          extension: fileData.extension || '',
          storagePath: fileData.storagePath || '',
          sourceEmailId: emailId,
          copiedFromEmail: true,
          classifiedAt: fileData.classifiedAt || null,
          originalUploadedAt: fileData.uploadedAt || null,
          extractedData: fileData.extractedData || null
        }

        await addDoc(shipmentFilesRef, shipmentFileData)

        // Actualizar el archivo original con la referencia al embarque
        const sourceFileRef = doc(db, 'Companies', companyStore.selectedCompanyId!, 'emails', emailId, 'files', fileDoc.id)
        await updateDoc(sourceFileRef, {
          copiedTo: arrayUnion({
            targetType: 'shipment',
            targetId: shipmentId,
            copiedAt: Timestamp.now()
          })
        })

        copiedCount++
      })

      await Promise.all(copyPromises)

      if (skippedCount > 0) {
      }
      return { success: true, copiedCount }
    } catch (err) {
      console.error('Error copiando archivos del email al embarque:', err)
      return { success: false, copiedCount: 0, error: err instanceof Error ? err.message : 'Error desconocido' }
    }
  }

  /**
   * Desvincula un email de su embarque asociado
   * Valida que no haya acciones aplicadas antes de desvincular
   */
  const unlinkEmailFromShipment = async (emailId: string): Promise<{ success: boolean; hasAppliedActions: boolean; appliedActionsCount?: number }> => {
    try {
      isLoading.value = true
      error.value = null

      if (!companyStore.selectedCompanyId) {
        throw new Error('No company selected')
      }

      const emailRef = doc(db, 'Companies', companyStore.selectedCompanyId, 'emails', emailId)
      const emailSnap = await getDoc(emailRef)

      if (!emailSnap.exists()) {
        throw new Error('Email no encontrado')
      }

      const emailData = emailSnap.data()

      if (emailData.matchedShipmentId) {
        await assertShipmentWritable(db, companyStore.selectedCompanyId, emailData.matchedShipmentId)
      }

      // Verificar si hay acciones aplicadas
      const appliedActions = emailData.parsed?.suggestedActions?.filter(
        (action: any) => action.status === 'applied'
      ) || []

      const hasAppliedActions = appliedActions.length > 0

      // Desvincular (se permite incluso con acciones aplicadas)
      await updateDoc(emailRef, {
        matchedShipmentId: null,
        matchedShipmentIdNumber: null,
        'parsed.references.matchedShipmentId': null,
        updatedAt: Timestamp.now()
      })


      return { 
        success: true, 
        hasAppliedActions,
        appliedActionsCount: appliedActions.length
      }
    } catch (err) {
      console.error('Error desvinculando email:', err)
      error.value = err instanceof Error ? err.message : 'Error desconocido'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Cambia la asociación de un email a un nuevo embarque
   * Resetea las acciones aplicadas a 'pending' y copia archivos al nuevo embarque
   */
  const changeShipmentAssociation = async (
    emailId: string,
    newShipmentId: string,
    newShipmentIdNumber: string
  ): Promise<{ success: boolean; resetActionsCount: number }> => {
    try {
      isLoading.value = true
      error.value = null

      if (!companyStore.selectedCompanyId) {
        throw new Error('No company selected')
      }

      const emailRef = doc(db, 'Companies', companyStore.selectedCompanyId, 'emails', emailId)
      const emailSnap = await getDoc(emailRef)

      if (!emailSnap.exists()) {
        throw new Error('Email no encontrado')
      }

      const emailData = emailSnap.data()

      await assertShipmentWritable(db, companyStore.selectedCompanyId, newShipmentId)
      if (emailData.matchedShipmentId && emailData.matchedShipmentId !== newShipmentId) {
        await assertShipmentWritable(db, companyStore.selectedCompanyId, emailData.matchedShipmentId)
      }

      let resetActionsCount = 0
      let updatedActions = emailData.parsed?.suggestedActions || []

      // Resetear TODAS las acciones (aplicadas y rechazadas) a 'pending'
      if (emailData.parsed?.suggestedActions) {
        updatedActions = emailData.parsed.suggestedActions.map((action: any) => {
          if (action.status === 'applied' || action.status === 'rejected') {
            resetActionsCount++
            return {
              ...action,
              status: 'pending',
              appliedAt: null,
              appliedBy: null,
              appliedByName: null,
              rejectedAt: null,
              rejectedBy: null,
              rejectedByName: null,
              rejectionReason: null
            }
          }
          return action
        })
      }

      // Actualizar TODO en una sola operación atómica
      await updateDoc(emailRef, {
        matchedShipmentId: newShipmentId,
        matchedShipmentIdNumber: newShipmentIdNumber,
        'parsed.suggestedActions': updatedActions,
        'parsed.references.matchedShipmentId': newShipmentId,
        updatedAt: Timestamp.now()
      })

      // Actualizar el nuevo embarque con los campos de envio de documentos originales
      const receivedAt = emailData.receivedAt || null
      const shipmentRef = doc(db, 'Companies', companyStore.selectedCompanyId, 'shipments', newShipmentId)
      const shipmentSnap = await getDoc(shipmentRef)
      const shipmentData = shipmentSnap.exists() ? shipmentSnap.data() : {}
      const pl3 = shipmentData.pl3 || ''
      const shipmentUpdate: Record<string, any> = {
        envio_documentos_originales_proveedor: pl3,
        updatedAt: Timestamp.now()
      }

      if (receivedAt) {
        if (receivedAt instanceof Date) {
          shipmentUpdate.envio_documentos_originales_fecha = Timestamp.fromDate(receivedAt)
        } else if (typeof receivedAt === 'string') {
          shipmentUpdate.envio_documentos_originales_fecha = Timestamp.fromDate(new Date(receivedAt))
        } else if (receivedAt && typeof receivedAt === 'object' && 'seconds' in receivedAt) {
          shipmentUpdate.envio_documentos_originales_fecha = receivedAt
        }
      }

      await updateDoc(shipmentRef, shipmentUpdate)

      return {
        success: true,
        resetActionsCount
      }
    } catch (err) {
      console.error('Error cambiando asociación de email:', err)
      error.value = err instanceof Error ? err.message : 'Error desconocido'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Desvincula una OC específica de un email
   * Remueve la OC del array matchedPurchaseOrders
   */
  const unlinkPurchaseOrderFromEmail = async (emailId: string, poId: string): Promise<{ success: boolean }> => {
    try {
      isLoading.value = true
      error.value = null

      if (!companyStore.selectedCompanyId) {
        throw new Error('No company selected')
      }

      const emailRef = doc(db, 'Companies', companyStore.selectedCompanyId, 'emails', emailId)
      const emailSnap = await getDoc(emailRef)

      if (!emailSnap.exists()) {
        throw new Error('Email no encontrado')
      }

      const emailData = emailSnap.data()
      const currentPOs = Array.isArray(emailData.matchedPurchaseOrders) ? emailData.matchedPurchaseOrders : []
      const updatedPOs = currentPOs.filter((po: any) => po.id !== poId)
      const updatedPOIds = updatedPOs.map((po: any) => po.id).filter(Boolean)

      await updateDoc(emailRef, {
        matchedPurchaseOrders: updatedPOs,
        matchedPurchaseOrderIds: updatedPOIds,
        updatedAt: Timestamp.now()
      })

      return { success: true }
    } catch (err) {
      console.error('Error desvinculando OC del email:', err)
      error.value = err instanceof Error ? err.message : 'Error desconocido'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Obtiene las órdenes de compra madre más recientes (carga inicial del modal)
   */
  const fetchAvailablePurchaseOrders = async (initialLimit = 20) => {
    try {
      isLoading.value = true
      error.value = null

      if (!companyStore.selectedCompanyId) {
        throw new Error('No company selected')
      }

      const purchaseOrdersRef = collection(db, 'Companies', companyStore.selectedCompanyId, 'purchaseOrders')
      const q = query(
        purchaseOrdersRef,
        orderBy('createdAt', 'desc'),
        limit(initialLimit)
      )
      const snapshot = await getDocs(q)

      return mapAndFilterParentPOs(snapshot)
    } catch (err) {
      console.error('❌ Error obteniendo órdenes de compra:', err)
      error.value = err instanceof Error ? err.message : 'Error desconocido'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Busca órdenes de compra en Firestore por número de OC (prefijo).
   * No tiene límite arbitrario: trae todas las coincidencias.
   */
  const searchPurchaseOrders = async (searchTerm: string) => {
    try {
      isLoading.value = true
      error.value = null

      if (!companyStore.selectedCompanyId) {
        throw new Error('No company selected')
      }

      const purchaseOrdersRef = collection(db, 'Companies', companyStore.selectedCompanyId, 'purchaseOrders')
      const term = searchTerm.toUpperCase()

      const q = query(
        purchaseOrdersRef,
        where('purchaseOrder', '>=', term),
        where('purchaseOrder', '<=', term + '\uf8ff')
      )
      const snapshot = await getDocs(q)

      return mapAndFilterParentPOs(snapshot)
    } catch (err) {
      console.error('❌ Error buscando órdenes de compra:', err)
      error.value = err instanceof Error ? err.message : 'Error desconocido'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Mapea documentos de Firestore a objetos de OC y filtra solo las madres
   */
  const mapAndFilterParentPOs = (snapshot: any) => {
    const purchaseOrders = snapshot.docs.map((doc: any) => {
      const data = doc.data()
      return {
        id: doc.id,
        purchaseOrder: data.purchaseOrder || doc.id,
        supplierName: data.supplierName || '',
        status: data.status || '',
        currency: data.currency || '',
        totalAmount: data.currencyTotalCashIncludingTax || data.totalAmount || 0,
        requestedDate: data.requestedDate || null,
        hasChildren: data.children && Array.isArray(data.children) && data.children.length > 0,
        childrenCount: data.children?.length || 0,
        createdAt: data.createdAt
      }
    })

    return purchaseOrders.filter((po: any) => !po.purchaseOrder?.includes('-P'))
  }

  /**
   * Asocia un email a una orden de compra y opcionalmente a sus hijas
   * @param associateToChildren - Si true, asocia también a todas las OCs hijas
   */
  const associateEmailToPurchaseOrder = async (
    emailId: string,
    purchaseOrderId: string,
    purchaseOrderNumber: string,
    associateToChildren: boolean = true
  ) => {
    try {
      isLoading.value = true
      error.value = null

      if (!companyStore.selectedCompanyId) {
        throw new Error('No company selected')
      }

      const emailRef = doc(db, 'Companies', companyStore.selectedCompanyId, 'emails', emailId)
      const emailSnap = await getDoc(emailRef)

      if (!emailSnap.exists()) {
        throw new Error('Email no encontrado')
      }

      const emailData = emailSnap.data()

      // Preparar array de OCs a asociar
      const purchaseOrdersToAssociate: Array<{ id: string; purchaseOrder: string; supplierName?: string }> = [
        { id: purchaseOrderId, purchaseOrder: purchaseOrderNumber }
      ]

      // Si se debe asociar a hijas, obtener las children de la OC
      if (associateToChildren) {
        try {
          const poRef = doc(db, 'Companies', companyStore.selectedCompanyId, 'purchaseOrders', purchaseOrderId)
          const poSnap = await getDoc(poRef)

          if (poSnap.exists()) {
            const poData = poSnap.data()
            if (poData.children && Array.isArray(poData.children)) {
              for (const child of poData.children) {
                const childId = typeof child === 'string' ? child : child.id
                const childPONumber = typeof child === 'string' ? child : (child.purchaseOrder || child.id)
                if (childId && !purchaseOrdersToAssociate.some(p => p.id === childId)) {
                  purchaseOrdersToAssociate.push({
                    id: childId,
                    purchaseOrder: childPONumber,
                    supplierName: poData.supplierName
                  })
                }
              }
            }
          }
        } catch (childError) {
          console.warn('⚠️ Error obteniendo hijas de la OC, continuando solo con la madre:', childError)
        }
      }

      // Obtener OCs actualmente asociadas
      const currentPOs = Array.isArray(emailData.matchedPurchaseOrders) ? emailData.matchedPurchaseOrders : []
      const currentPOIds = new Set(currentPOs.map((po: any) => po.id))

      // Agregar nuevas OCs que no estén ya asociadas
      const newPOs = purchaseOrdersToAssociate.filter(po => !currentPOIds.has(po.id))
      const updatedPOs = [...currentPOs, ...newPOs]
      const updatedPOIds = updatedPOs.map((po: any) => po.id).filter(Boolean)

      // Actualizar el email con todas las OCs
      await updateDoc(emailRef, {
        matchedPurchaseOrders: updatedPOs,
        matchedPurchaseOrderIds: updatedPOIds,
        updatedAt: Timestamp.now()
      })


      return {
        success: true,
        associatedCount: newPOs.length,
        associatedPOs: newPOs
      }
    } catch (err) {
      console.error('❌ Error asociando email a orden de compra:', err)
      error.value = err instanceof Error ? err.message : 'Error desconocido'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Copia los archivos adjuntos del email a la subcolección Documents de la orden de compra.
   * Verifica duplicados por sourceEmailId + fileName para evitar copias repetidas.
   */
  const copyEmailAttachmentsToPurchaseOrder = async (emailId: string, purchaseOrderId: string, fileIds?: string[]) => {
    try {
      if (!companyStore.selectedCompanyId || !currentUser.value) {
        console.warn('No company or user available for copying attachments')
        return { success: false, copiedCount: 0 }
      }

      // Obtener archivos de la subcolección files del email
      const emailFilesRef = collection(db, 'Companies', companyStore.selectedCompanyId, 'emails', emailId, 'files')
      const emailFilesSnapshot = await getDocs(emailFilesRef)

      if (emailFilesSnapshot.empty) {
        return { success: true, copiedCount: 0 }
      }

      // Referencia a la subcolección Documents de la OC
      const poDocumentsRef = collection(db, 'Companies', companyStore.selectedCompanyId, 'purchaseOrders', purchaseOrderId, 'Documents')

      // Verificar archivos ya copiados desde este email para evitar duplicados
      const existingFromEmailQuery = query(poDocumentsRef, where('sourceEmailId', '==', emailId))
      const existingSnapshot = await getDocs(existingFromEmailQuery)
      const existingFileNames = new Set(
        existingSnapshot.docs.map(d => d.data().fileName)
      )

      // Si se especifican fileIds, solo copiar esos archivos
      const fileDocs = fileIds
        ? emailFilesSnapshot.docs.filter(d => fileIds.includes(d.id))
        : emailFilesSnapshot.docs

      let copiedCount = 0
      let skippedCount = 0
      const copyPromises = fileDocs.map(async (fileDoc) => {
        const fileData = fileDoc.data()
        const fileName = fileData.filename || 'archivo_sin_nombre'

        // Saltar si ya existe un archivo con el mismo nombre copiado desde este email
        if (existingFileNames.has(fileName)) {
          skippedCount++
          return
        }

        const poDocumentData = {
          fileName,
          fileUrl: fileData.downloadUrl || '',
          fileSize: fileData.size || 0,
          mimeType: resolveMimeType(fileData.type || fileData.metadata?.originalContentType, fileName),
          fileType: fileData.fileType || 'other',
          uploadedAt: Timestamp.now(),
          createdBy: currentUser.value?.uid || '',
          uploadedBy: `${currentUser.value?.name || 'Sistema'} ${currentUser.value?.lastName || ''}`.trim(),
          storagePath: fileData.storagePath || '',
          sourceEmailId: emailId,
          copiedFromEmail: true,
          extension: fileData.extension || '',
          extractedData: fileData.extractedData || null,
          classifiedAt: fileData.classifiedAt || null
        }

        await addDoc(poDocumentsRef, poDocumentData)

        // Actualizar el archivo original con la referencia a la OC
        const sourceFileRef = doc(db, 'Companies', companyStore.selectedCompanyId!, 'emails', emailId, 'files', fileDoc.id)
        await updateDoc(sourceFileRef, {
          copiedTo: arrayUnion({
            targetType: 'purchaseOrder',
            targetId: purchaseOrderId,
            copiedAt: Timestamp.now()
          })
        })

        copiedCount++
      })

      await Promise.all(copyPromises)

      if (skippedCount > 0) {
      }
      return { success: true, copiedCount }
    } catch (err) {
      console.error('Error copiando archivos del email a la orden de compra:', err)
      return { success: false, copiedCount: 0, error: err instanceof Error ? err.message : 'Error desconocido' }
    }
  }

  /**
   * Asocia SOLO la respuesta que se acaba de enviar (remitente = mi buzón, posterior al envío)
   * al embarque/OC del contexto. Antes asociaba todos los correos sin asociar del conversationId,
   * y Outlook mantiene el conversationId aunque el forwarder cambie de operación: así se pegaban
   * correos de otras importaciones (KILA-467). El resto del hilo lo hereda el backend con su guarda.
   * Incluye retry porque el reply puede tardar en sincronizarse a Firestore.
   */
  const associateReplyToContext = async (
    originalEmailId: string,
    contextModule: 'shipment' | 'purchaseOrder',
    contextEntityId: string,
    sentAfter: Date,
    mailboxEmail: string
  ) => {
    try {
      if (!mailboxEmail?.trim()) {
        console.warn('[associateReplyToContext] Sin buzón conectado, no se puede reconocer la respuesta')
        return
      }

      if (!companyStore.selectedCompanyId) {
        console.warn('[associateReplyToContext] No company selected')
        return
      }

      const emailRef = doc(db, 'Companies', companyStore.selectedCompanyId, 'emails', originalEmailId)
      const emailSnap = await getDoc(emailRef)

      if (!emailSnap.exists()) {
        console.warn('[associateReplyToContext] Email original no encontrado:', originalEmailId)
        return
      }

      const originalData = emailSnap.data()
      const conversationId = originalData.conversationId

      if (!conversationId) {
        console.warn('[associateReplyToContext] Email sin conversationId, no se puede expandir hilo')
        return
      }

      const emailsRef = collection(db, 'Companies', companyStore.selectedCompanyId, 'emails')

      const attemptAssociation = async (): Promise<number> => {
        const convQuery = query(emailsRef, where('conversationId', '==', conversationId))
        const convSnapshot = await getDocs(convQuery)

        const candidatos = convSnapshot.docs.map(d => ({ id: d.id, ...d.data() })) as any[]
        const reply = pickOwnSentEmail(candidatos, mailboxEmail, sentAfter, originalEmailId)
        if (!reply) return 0

        if (contextModule === 'shipment') {
          if (reply.matchedShipmentId) return 0

          await updateDoc(doc(emailsRef, reply.id), {
            matchedShipmentId: contextEntityId,
            matchedShipmentIdNumber: originalData.matchedShipmentIdNumber || contextEntityId,
            updatedAt: Timestamp.now()
          })
          return 1
        }

        const alreadyLinked = Array.isArray(reply.matchedPurchaseOrders) &&
          reply.matchedPurchaseOrders.some((po: any) => po.id === contextEntityId)
        if (alreadyLinked) return 0

        const originalPOs = Array.isArray(originalData.matchedPurchaseOrders) ? originalData.matchedPurchaseOrders : []
        const originalPOIds = Array.isArray(originalData.matchedPurchaseOrderIds) ? originalData.matchedPurchaseOrderIds : []

        await updateDoc(doc(emailsRef, reply.id), {
          matchedPurchaseOrders: originalPOs,
          matchedPurchaseOrderIds: originalPOIds,
          updatedAt: Timestamp.now()
        })
        return 1
      }

      // Primer intento después de 2 segundos (esperar sync del reply)
      await new Promise(resolve => setTimeout(resolve, 2000))
      let count = await attemptAssociation()

      // Si no se asoció nada, reintentar una vez más después de 3 segundos
      if (count === 0) {
        await new Promise(resolve => setTimeout(resolve, 3000))
        count = await attemptAssociation()
      }

      if (count > 0) {
      }
    } catch (err) {
      console.error('[associateReplyToContext] Error:', err)
    }
  }

  /**
   * Asocia un correo NUEVO (no reply) enviado desde un módulo.
   * Busca el email más reciente del usuario con el mismo subject y lo asocia al embarque/OC.
   * Incluye retry porque el email tarda en sincronizarse a Firestore.
   */
  const associateNewEmailToContext = async (
    subject: string,
    contextModule: 'shipment' | 'purchaseOrder',
    contextEntityId: string,
    sentAfter: Date,
    mailboxEmail: string,
    contextEntityIdNumber?: string
  ) => {
    try {
      if (!mailboxEmail?.trim()) {
        console.warn('[associateNewEmailToContext] Sin buzón conectado, no se puede reconocer el correo enviado')
        return
      }

      if (!companyStore.selectedCompanyId) {
        console.warn('[associateNewEmailToContext] No company selected')
        return
      }

      const emailsRef = collection(db, 'Companies', companyStore.selectedCompanyId, 'emails')

      const attemptFind = async (): Promise<boolean> => {
        const recentQuery = query(
          emailsRef,
          where('subject', '==', subject),
          orderBy('receivedAt', 'desc'),
          limit(5)
        )
        const snapshot = await getDocs(recentQuery)

        const sent = pickOwnSentEmail(
          snapshot.docs.map(d => ({ id: d.id, ...d.data() })) as any[],
          mailboxEmail,
          sentAfter
        )
        if (!sent) return false

        if (contextModule === 'shipment') {
          if (sent.matchedShipmentId) return false
          await updateDoc(doc(emailsRef, sent.id), {
            matchedShipmentId: contextEntityId,
            matchedShipmentIdNumber: contextEntityIdNumber || contextEntityId,
            updatedAt: Timestamp.now()
          })
          return true
        }

        const alreadyLinked = Array.isArray(sent.matchedPurchaseOrders) &&
          sent.matchedPurchaseOrders.some((po: any) => po.id === contextEntityId)
        if (alreadyLinked) return false

        const poEntry = { id: contextEntityId, purchaseOrder: contextEntityIdNumber || contextEntityId }
        await updateDoc(doc(emailsRef, sent.id), {
          matchedPurchaseOrders: arrayUnion(poEntry),
          matchedPurchaseOrderIds: arrayUnion(contextEntityId),
          updatedAt: Timestamp.now()
        })
        return true
      }

      await new Promise(resolve => setTimeout(resolve, 3000))
      let found = await attemptFind()

      if (!found) {
        await new Promise(resolve => setTimeout(resolve, 4000))
        found = await attemptFind()
      }
    } catch (err) {
      console.error('[associateNewEmailToContext] Error:', err)
    }
  }

  return {
    isLoading,
    error,
    associateEmailToShipment,
    associateThreadToShipment,
    createBlankShipmentFromEmail,
    fetchAvailableShipments,
    searchShipments,
    fetchShipmentContainers,
    fetchShipmentContainersBatch,
    unlinkEmailFromShipment,
    unlinkPurchaseOrderFromEmail,
    changeShipmentAssociation,
    fetchAvailablePurchaseOrders,
    searchPurchaseOrders,
    associateEmailToPurchaseOrder,
    copyEmailAttachmentsToPurchaseOrder,
    associateReplyToContext,
    associateNewEmailToContext
  }
}
